'use strict';

import addDays from 'date-fns/addDays/index.js';
import moment from 'moment-timezone';

/**
 * Formats a date for use in query params
 * for api calls. This removes the time
 * part of the date.
 * @param {Date} date
 * @returns {string} formatted date
 */
export const formatDateForAPIQueryParam = (date) => {
  return getYearMonthDayString(date);
};

/**
 * Returns a new date relative to the passed date,
 * and adds/removes days from it.
 * @param {Date} date
 * @param {number} days postive or negative number of days to adjust date by
 * @returns {Date}
 */
export const getDateWithAdjustedDays = (date, days) => {
  return addDays(date, days);
};

/**
 * Get end of day date
 * @param  {Date} date
 * @return {Date}
 */
export const getEndOfDayDate = (date) => {
  const newDate = new Date(date); // Avoid mutating original date object
  return new Date(newDate.setHours(23, 59, 59, 999));
};

/**
 * Get locale short date string. E.g.:
 * en-GB = '14/03/21'
 * sv-SE = '21-03-14'
 * @param  {Date} date
 * @param  {string} locale
 * @return {string}
 */
export const getLocaleShortDateString = (date, locale) => {
  return date.toLocaleDateString(locale, { year: '2-digit', month: '2-digit', day: '2-digit' });
};

/**
 * Get start of day date
 * @param  {Date} date
 * @return {Date}
 */
export const getStartOfDayDate = (date) => {
  const newDate = new Date(date); // Avoid mutating original date object
  return new Date(newDate.setHours(0, 0, 0, 0));
};

/**
 * Gets a string in format yyyy-mm-dd
 * from a date, adhering to current locale.
 * @param {Date} date
 * @returns {string} date in format yyyy-mm-dd
 */
export const getYearMonthDayString = (date) => {
  if (!isValidDate(date)) throw new Error(`Invalid date provided: ${date}`);

  const offsetDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60 * 1000));
  const dateParts = offsetDate.toISOString().split('T');
  return dateParts[0];
};

/**
 * Check if date is a valid javascript date
 * @param {any} date
 * @returns {boolean}
 */
export const isValidDate = (date) => {
  if (!date || !(date instanceof Date)) return false;

  // If the date object is invalid it
  // will return 'NaN' on getTime()
  // and NaN is never equal to itself.
  // Confusingly enough this is more accurate
  // than the isNaN function according to
  // this article:
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/isNaN
  // eslint-disable-next-line
  return date.getTime() === date.getTime();
};

const _dateTimeFormatters = {};
/**
 * @private
 * @param {string} locale
 * @param {string} [timeZone]
 * @returns {Intl.DateTimeFormat}
 */
const _getDateTimeFormatter = (locale, timeZone) => {
  const key = `${locale}-${timeZone}`;

  if (!_dateTimeFormatters[key]) {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZone,
    };

    _dateTimeFormatters[key] = new Intl.DateTimeFormat(locale, options);
  }
  return _dateTimeFormatters[key];
};

/**
 * Get formatted date time string
 * @param  {Date | number} date
 * @param  {string} locale
 * @param  {string} [timeZone]
 * @return {string}
 **/
export const getFormattedDateTimeString = (date, locale, timeZone) => {
  if (!date) return '';

  return _getDateTimeFormatter(locale, timeZone).format(date);
};

/**
 * Converts a value to a Date object.
 * If the value is already a valid Date object, it is returned as is.
 * If the value has a "_seconds" or "seconds" property, it is treated as a timestamp and converted to a Date object.
 * If the value is not a valid Date or timestamp, null is returned.
 *
 * @param {*} maybeDate - The value to convert to a Date object.
 * @returns {Date|null} - The converted Date object, or null if the value is not a valid Date or timestamp.
 */
export const convertToDate = (maybeDate) => {
  if (isValidDate(maybeDate)) return maybeDate;

  const seconds = maybeDate?._seconds || maybeDate?.seconds;
  if (seconds) return new Date(seconds * 1000);

  return null;
};

export const getStartDayOfWeek = (locale) => {
  if (!locale) {
    try {
      locale = Intl.DateTimeFormat().resolvedOptions().locale;
    } catch {
      locale = navigator.language || 'en-US';
    }
  }
  try {
    const localeObj = new Intl.Locale(locale);
    const { firstDay } = localeObj.weekInfo;
    // There is some discrepancies between Intl.Locale.weekInfo and Date.getDay with what number is reprensenting which day...
    // - Intl.Locale.weekInfo.firstDay - An integer between 1 (Monday) and 7 (Sunday) indicating the first day of the week for the locale. Commonly 1, 5, 6, or 7.
    // - Date.getDay - 0 for Sunday, 1 for Monday, 2 for Tuesday, and so on
    // So we need to translate from Intl.Locale.weekInfo to Date.getDay representation moving sunday from 7 to 0...
    return firstDay % 7;
  } catch {
    return 0;
  }
};

export const startOfDateUtcInTz = (date, timeZone) => {
  // Create a date for the start of the day in the target timezone, even thus the date is in users local timezone we use
  // it as the selected day as the start of the day in the target timezone
  const startOfDate = moment(date).startOf('day').format('YYYY-MM-DD HH:mm:ss');
  const startOfDateInTz = moment.tz(startOfDate, timeZone);

  // Convert the timezoned start of the day to UTC (ex. when it's 00:00 in the target timezone we want to know the
  // actual time in UTC)
  const startOfDateUtcInTz = moment(startOfDateInTz).tz('UTC');

  return new Date(startOfDateUtcInTz.format());
};
